import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { API } from '@/App';
import { toast } from 'sonner';
import {
  Upload,
  FileSpreadsheet,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Download,
  FileText,
  Table,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Info,
  Loader2,
  X,
  Search,
  Clock,
  CheckCircle2,
  XCircle
} from 'lucide-react';

// مكون أيقونة الترتيب - خارج المكون الرئيسي لتجنب إعادة الإنشاء
const SortIcon = ({ field, sortBy, sortOrder }) => {
  if (sortBy !== field) return null;
  return sortOrder === 'desc' ? 
    <ChevronDown className="w-4 h-4 inline-block mr-1" /> : 
    <ChevronUp className="w-4 h-4 inline-block mr-1" />;
};

const PriceAnalysis = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [sortBy, setSortBy] = useState('price_diff');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [taskId, setTaskId] = useState(null);
  const [taskStatus, setTaskStatus] = useState(null);
  const pollingRef = useRef(null);

  // تنظيف polling عند إلغاء التحميل أو الخروج
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // وظيفة متابعة حالة المهمة
  const pollTaskStatus = useCallback(async (id) => {
    try {
      const response = await fetch(`${API}/analysis/task/${id}`);
      if (!response.ok) {
        throw new Error('فشل في جلب حالة المهمة');
      }
      
      const task = await response.json();
      setTaskStatus(task);
      setUploadProgress(task.progress || 0);
      setLoadingMessage(task.message || 'جاري التحليل...');
      
      if (task.status === 'completed') {
        // إيقاف polling
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        
        setAnalysisResult(task.result);
        setIsLoading(false);
        setTaskId(null);
        setTaskStatus(null);
        
        const totalDuplicates = task.result?.analysis?.reduce(
          (sum, sheet) => sum + (sheet.duplicate_codes || 0), 0
        ) || 0;
        
        if (totalDuplicates > 0) {
          toast.success(`تم العثور على ${totalDuplicates} كود مكرر مع فروقات في الأسعار`);
        } else {
          toast.info('لم يتم العثور على أكواد مكررة مع فروقات في الأسعار');
        }
        
      } else if (task.status === 'failed') {
        // إيقاف polling
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        
        setIsLoading(false);
        setTaskId(null);
        setTaskStatus(null);
        toast.error(task.error || 'فشل في تحليل الملف');
      }
      
    } catch (error) {
      console.error('Polling error:', error);
    }
  }, []);

  // بدء المهمة غير المتزامنة
  const startAsyncAnalysis = async (file, formData) => {
    try {
      setLoadingMessage('جاري رفع الملف...');
      
      // رفع الملف وبدء التحليل في الخلفية
      const response = await fetch(`${API}/analysis/upload/async`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'فشل في بدء التحليل');
      }
      
      const data = await response.json();
      
      if (data.success && data.task_id) {
        setTaskId(data.task_id);
        setUploadProgress(10);
        setLoadingMessage('تم بدء التحليل في الخلفية...');
        
        // بدء متابعة حالة المهمة
        pollingRef.current = setInterval(() => {
          pollTaskStatus(data.task_id);
        }, 1000); // كل ثانية
        
        // جلب الحالة الأولى فوراً
        pollTaskStatus(data.task_id);
        
      } else {
        throw new Error('فشل في بدء المهمة');
      }
      
    } catch (error) {
      setIsLoading(false);
      toast.error(error.message);
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    if (!file.name.match(/\.(xlsx|xls|pdf)$/i)) {
      toast.error('يجب أن يكون الملف بصيغة Excel (.xlsx, .xls) أو PDF (.pdf)');
      return;
    }

    setIsLoading(true);
    setUploadProgress(0);
    setAnalysisResult(null);
    
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    const isPDF = file.name.toLowerCase().endsWith('.pdf');
    const isLargeFile = file.size > 5 * 1024 * 1024; // أكبر من 5 MB
    
    const formData = new FormData();
    formData.append('file', file);

    // استخدام الوضع غير المتزامن للملفات الكبيرة أو PDF
    if (isLargeFile || isPDF) {
      setLoadingMessage(`جاري رفع ملف ${isPDF ? 'PDF' : 'Excel'} (${fileSizeMB} MB)...`);
      await startAsyncAnalysis(file, formData);
      return;
    }
    
    // الوضع المتزامن للملفات الصغيرة
    setLoadingMessage(`جاري تحليل ملف Excel (${fileSizeMB} MB)...`);
    
    try {
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded / e.total) * 50);
            setUploadProgress(progress);
            if (progress >= 50) {
              setLoadingMessage('جاري تحليل البيانات...');
            }
          }
        });
        
        xhr.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = 50 + Math.round((e.loaded / e.total) * 50);
            setUploadProgress(progress);
          }
        });
        
        xhr.addEventListener('load', () => {
          setUploadProgress(100);
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(new Error('خطأ في قراءة الاستجابة'));
            }
          } else {
            try {
              const error = JSON.parse(xhr.responseText);
              reject(new Error(error.detail || `خطأ: ${xhr.status}`));
            } catch (e) {
              reject(new Error(`فشل في تحليل الملف - كود: ${xhr.status}`));
            }
          }
        });
        
        xhr.addEventListener('error', () => reject(new Error('فشل في الاتصال')));
        xhr.addEventListener('timeout', () => reject(new Error('انتهت المهلة')));
        
        xhr.open('POST', `${API}/analysis/upload`);
        xhr.timeout = 120000;
        xhr.send(formData);
      });

      setAnalysisResult(result);
      
      const totalDuplicates = result.analysis?.reduce(
        (sum, sheet) => sum + (sheet.duplicate_codes || 0), 0
      ) || 0;
      
      if (totalDuplicates > 0) {
        toast.success(`تم العثور على ${totalDuplicates} كود مكرر مع فروقات في الأسعار`);
      } else {
        toast.info('لم يتم العثور على أكواد مكررة مع فروقات في الأسعار');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
      setUploadProgress(0);
    }
  }, [pollTaskStatus]);

  // إلغاء التحليل
  const cancelAnalysis = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setIsLoading(false);
    setTaskId(null);
    setTaskStatus(null);
    setLoadingMessage('');
    setUploadProgress(0);
    toast.info('تم إلغاء التحليل');
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/pdf': ['.pdf']
    },
    maxFiles: 1
  });

  const toggleItemExpand = (code) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(code)) {
      newExpanded.delete(code);
    } else {
      newExpanded.add(code);
    }
    setExpandedItems(newExpanded);
  };

  const getSortedItems = (items) => {
    if (!items) return [];
    
    // Filter by search query
    let filtered = items;
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      filtered = items.filter(item => 
        item.code?.toLowerCase().includes(query) ||
        item.name?.toLowerCase().includes(query)
      );
    }
    
    // Sort
    return [...filtered].sort((a, b) => {
      const aVal = a[sortBy] || 0;
      const bVal = b[sortBy] || 0;
      return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
    });
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const exportToPDF = async () => {
    if (!analysisResult) return;
    
    try {
      const response = await fetch(`${API}/analysis/export/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(analysisResult)
      });

      if (!response.ok) throw new Error('فشل في تصدير PDF');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `price_analysis_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('تم تصدير التقرير بنجاح');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const exportToExcel = async () => {
    if (!analysisResult) return;
    
    try {
      const response = await fetch(`${API}/analysis/export/excel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(analysisResult)
      });

      if (!response.ok) throw new Error('فشل في تصدير Excel');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `price_analysis_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('تم تصدير التقرير بنجاح');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const clearAnalysis = () => {
    setAnalysisResult(null);
    setExpandedItems(new Set());
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-l from-orange-500 to-amber-600 rounded-2xl p-6 mb-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
              <BarChart3 className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">تحليل فروقات الأسعار</h1>
              <p className="text-white/80 text-sm mt-1">
                تحليل ملفات Excel للكشف عن تغيرات الأسعار للأصناف المكررة
              </p>
            </div>
          </div>
          {analysisResult && (
            <button
              onClick={clearAnalysis}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
              تحليل جديد
            </button>
          )}
        </div>
      </div>

      {/* Upload Zone */}
      {!analysisResult && (
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all
            ${isDragActive 
              ? 'border-orange-500 bg-orange-50' 
              : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50/50'
            }
            ${isLoading ? 'pointer-events-none' : ''}
          `}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center gap-4">
            {isLoading ? (
              <>
                <div className="relative">
                  <Loader2 className="w-16 h-16 text-orange-500 animate-spin" />
                  {taskId && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                      <Clock className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
                
                {/* شريط التقدم المحسّن */}
                <div className="w-full max-w-md">
                  <div className="bg-gray-200 rounded-full h-4 overflow-hidden shadow-inner">
                    <div 
                      className="bg-gradient-to-r from-orange-500 to-amber-500 h-full rounded-full transition-all duration-500 relative"
                      style={{ width: `${uploadProgress}%` }}
                    >
                      <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm font-medium text-orange-600">{uploadProgress}%</span>
                    {taskId && (
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        معالجة في الخلفية
                      </span>
                    )}
                  </div>
                </div>
                
                {/* رسالة الحالة */}
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">
                    {taskStatus?.status === 'processing' ? 'جاري تحليل الملف...' : 'جاري رفع الملف...'}
                  </h3>
                  <p className="text-gray-500 text-sm">
                    {loadingMessage || 'يرجى الانتظار'}
                  </p>
                  {taskStatus?.filename && (
                    <p className="text-xs text-gray-400 mt-1">
                      {taskStatus.filename} ({taskStatus.file_size_mb} MB)
                    </p>
                  )}
                </div>
                
                {/* زر الإلغاء */}
                {taskId && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      cancelAnalysis();
                    }}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors text-sm flex items-center gap-2"
                  >
                    <XCircle className="w-4 h-4" />
                    إلغاء التحليل
                  </button>
                )}
              </>
            ) : (
              <div className="w-20 h-20 bg-orange-100 rounded-2xl flex items-center justify-center">
                <Upload className="w-10 h-10 text-orange-600" />
              </div>
            )}
            {!isLoading && (
              <>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">
                    اسحب ملف Excel أو PDF هنا
                  </h3>
                  <p className="text-gray-500">أو انقر للاختيار</p>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <FileSpreadsheet className="w-4 h-4" />
                    <span>يدعم ملفات Excel (.xlsx, .xls) و PDF (.pdf)</span>
                  </div>
                  <p className="text-xs text-gray-400">
                    ✓ يدعم الملفات الكبيرة حتى 100 ميجابايت
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Analysis Results */}
      {analysisResult && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">اسم الملف</p>
                  <p className="font-semibold text-gray-800 truncate max-w-[150px]">
                    {analysisResult.filename}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Table className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">إجمالي الأصناف</p>
                  <p className="font-semibold text-gray-800">
                    {analysisResult.analysis?.reduce((sum, s) => sum + s.total_items, 0) || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">أكواد مكررة</p>
                  <p className="font-semibold text-gray-800">
                    {analysisResult.analysis?.reduce((sum, s) => sum + s.duplicate_codes, 0) || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">أكبر نسبة تغيير</p>
                  <p className="font-semibold text-gray-800">
                    {Math.max(...(analysisResult.analysis?.map(s => s.summary?.max_increase_percent || 0) || [0])).toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Export Buttons and Search */}
          <div className="flex gap-3 justify-between items-center flex-wrap">
            {/* Search Box */}
            <div className="relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="بحث بالكود أو الاسم..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 w-64"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Export Buttons */}
            <div className="flex gap-3">
              <button
                onClick={exportToPDF}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                <FileText className="w-4 h-4" />
                تصدير PDF
              </button>
              <button
                onClick={exportToExcel}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                title="تصدير Excel بمعادلات ديناميكية - عدّل الأسعار وستتحدث الحسابات تلقائياً"
              >
                <Download className="w-4 h-4" />
                تصدير Excel ⚡
              </button>
            </div>
          </div>

          {/* Results Table */}
          {analysisResult.analysis?.map((sheetData, sheetIndex) => (
            <div key={sheetIndex} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800">
                    <FileSpreadsheet className="w-4 h-4 inline-block ml-2 text-gray-500" />
                    {sheetData.sheet_name}
                  </h3>
                  <span className="text-sm text-gray-500">
                    {searchQuery ? (
                      <>
                        {getSortedItems(sheetData.items_with_price_changes).length} من {sheetData.items_with_price_changes?.length || 0} صنف
                      </>
                    ) : (
                      <>{sheetData.items_with_price_changes?.length || 0} صنف مع فروقات في الأسعار</>
                    )}
                  </span>
                </div>
              </div>

              {sheetData.items_with_price_changes?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">الكود</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">اسم الصنف</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">الوحدة</th>
                        <th 
                          className="px-4 py-3 text-right text-sm font-semibold text-gray-600 cursor-pointer hover:text-orange-600"
                          onClick={() => handleSort('occurrences')}
                        >
                          <SortIcon field="occurrences" sortBy={sortBy} sortOrder={sortOrder} />
                          التكرار
                        </th>
                        <th 
                          className="px-4 py-3 text-right text-sm font-semibold text-gray-600 cursor-pointer hover:text-orange-600"
                          onClick={() => handleSort('min_price')}
                        >
                          <SortIcon field="min_price" sortBy={sortBy} sortOrder={sortOrder} />
                          أقل سعر
                        </th>
                        <th 
                          className="px-4 py-3 text-right text-sm font-semibold text-gray-600 cursor-pointer hover:text-orange-600"
                          onClick={() => handleSort('max_price')}
                        >
                          <SortIcon field="max_price" sortBy={sortBy} sortOrder={sortOrder} />
                          أعلى سعر
                        </th>
                        <th 
                          className="px-4 py-3 text-right text-sm font-semibold text-gray-600 cursor-pointer hover:text-orange-600"
                          onClick={() => handleSort('price_diff')}
                        >
                          <SortIcon field="price_diff" sortBy={sortBy} sortOrder={sortOrder} />
                          فرق السعر
                        </th>
                        <th 
                          className="px-4 py-3 text-right text-sm font-semibold text-gray-600 cursor-pointer hover:text-orange-600"
                          onClick={() => handleSort('percent_change')}
                        >
                          <SortIcon field="percent_change" sortBy={sortBy} sortOrder={sortOrder} />
                          نسبة التغيير
                        </th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">التفاصيل</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {getSortedItems(sheetData.items_with_price_changes).map((item, index) => (
                        <React.Fragment key={`${sheetIndex}-${item.code}-${index}`}>
                          <tr className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 text-sm text-gray-800 font-mono">{item.code}</td>
                            <td className="px-4 py-3 text-sm text-gray-800">{item.name}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{item.unit}</td>
                            <td className="px-4 py-3 text-sm text-gray-800 text-center">
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                                {item.occurrences}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-green-600 font-semibold">
                              {item.min_price?.toLocaleString('ar-SA', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-4 py-3 text-sm text-red-600 font-semibold">
                              {item.max_price?.toLocaleString('ar-SA', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-4 py-3 text-sm font-semibold">
                              <span className={`px-2 py-1 rounded-lg ${
                                item.price_diff > 100 ? 'bg-red-100 text-red-700' : 
                                item.price_diff > 10 ? 'bg-amber-100 text-amber-700' : 
                                'bg-green-100 text-green-700'
                              }`}>
                                {item.price_diff?.toLocaleString('ar-SA', { minimumFractionDigits: 2 })}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 py-1 rounded-lg font-semibold ${
                                item.percent_change > 50 ? 'bg-red-100 text-red-700' : 
                                item.percent_change > 20 ? 'bg-amber-100 text-amber-700' : 
                                'bg-green-100 text-green-700'
                              }`}>
                                {item.percent_change?.toFixed(1)}%
                                {item.percent_change > 0 && <TrendingUp className="w-3 h-3 inline-block mr-1" />}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => toggleItemExpand(item.code)}
                                className="p-1 hover:bg-gray-100 rounded transition-colors"
                              >
                                {expandedItems.has(item.code) ? 
                                  <ChevronUp className="w-5 h-5 text-gray-500" /> : 
                                  <ChevronDown className="w-5 h-5 text-gray-500" />
                                }
                              </button>
                            </td>
                          </tr>
                          {expandedItems.has(item.code) && (
                            <tr>
                              <td colSpan="9" className="px-4 py-3 bg-gray-50">
                                <div className="bg-white rounded-lg p-4 border border-gray-200">
                                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                    <Info className="w-4 h-4 text-blue-500" />
                                    تفاصيل سجل الأسعار - {item.name || item.code}
                                  </h4>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="bg-gray-100">
                                          <th className="px-3 py-2 text-right text-gray-600">#</th>
                                          <th className="px-3 py-2 text-right text-gray-600">رقم الفاتورة</th>
                                          <th className="px-3 py-2 text-right text-gray-600">التاريخ</th>
                                          <th className="px-3 py-2 text-right text-gray-600">الكمية</th>
                                          <th className="px-3 py-2 text-right text-gray-600">سعر الوحدة</th>
                                          <th className="px-3 py-2 text-right text-gray-600">السعر السابق</th>
                                          <th className="px-3 py-2 text-right text-gray-600">التغيير</th>
                                          <th className="px-3 py-2 text-right text-gray-600">الحالة</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {item.price_history?.map((history, hIdx) => (
                                          <tr key={hIdx} className={`border-b border-gray-100 hover:bg-gray-50 ${
                                            history.change_type === 'increase' ? 'bg-red-50' :
                                            history.change_type === 'decrease' ? 'bg-green-50' :
                                            ''
                                          }`}>
                                            <td className="px-3 py-2 text-gray-800">{history.index}</td>
                                            <td className="px-3 py-2 text-gray-800 font-mono">
                                              {history.invoice || '-'}
                                            </td>
                                            <td className="px-3 py-2 text-gray-600">
                                              {history.date || '-'}
                                            </td>
                                            <td className="px-3 py-2 text-gray-800">
                                              {history.quantity?.toLocaleString('ar-SA')}
                                            </td>
                                            <td className="px-3 py-2 font-semibold text-blue-600">
                                              {history.unit_price?.toLocaleString('ar-SA', { minimumFractionDigits: 2 })}
                                            </td>
                                            <td className="px-3 py-2 text-gray-500">
                                              {history.prev_price ? history.prev_price?.toLocaleString('ar-SA', { minimumFractionDigits: 2 }) : '-'}
                                            </td>
                                            <td className="px-3 py-2">
                                              {history.change_type !== 'first' && (
                                                <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                                  history.change_type === 'increase' ? 'bg-red-100 text-red-700' :
                                                  history.change_type === 'decrease' ? 'bg-green-100 text-green-700' :
                                                  'bg-gray-100 text-gray-600'
                                                }`}>
                                                  {history.change_amount > 0 ? '+' : ''}{history.change_amount?.toFixed(2)} ({history.change_percent?.toFixed(1)}%)
                                                </span>
                                              )}
                                            </td>
                                            <td className="px-3 py-2">
                                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                                history.change_type === 'increase' ? 'bg-red-100 text-red-700' :
                                                history.change_type === 'decrease' ? 'bg-green-100 text-green-700' :
                                                history.change_type === 'first' ? 'bg-blue-100 text-blue-700' :
                                                'bg-gray-100 text-gray-600'
                                              }`}>
                                                {history.change_type === 'increase' && '⬆️ ارتفاع'}
                                                {history.change_type === 'decrease' && '⬇️ انخفاض'}
                                                {history.change_type === 'first' && '🔵 أول شراء'}
                                                {history.change_type === 'same' && '➡️ ثابت'}
                                              </span>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>لا توجد أكواد مكررة في هذه الورقة</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PriceAnalysis;
